import {Image} from 'coreClasses/images/Image';
import {generateImagePlaceholder, getFrontEndConfig} from 'coreUtils/utils';
import {PRODUCT_IMAGES_SETTINGS} from 'brandConstants';

import {IThronProductImageConfig} from './IThronProductImage';
import {IProductTransformationObject} from 'coreClasses/product/IProduct';
import {TBreakpoint} from 'coreClasses/product/Product/ProductImage';
import ViewportMgr from 'coreClasses/view/ViewportMgr';

/**
 * Image implementation for Thron CDN product assets.
 * Builds the delivery URL internally from contentId + productId.
 * Supports server-side resizing via the ?w= query parameter,
 * enabling responsive <source> elements per breakpoint/DPI.
 *
 * Used on both PDP (productPage settings) and PLP (productTile settings).
 */
class ThronProductImage implements Image {
	private readonly imageUrl: string;
	private readonly alt: string;
	private readonly transformationObject: IProductTransformationObject;

	/**
	 * @param config.contentId - Thron content UUID
	 * @param config.productId - Product identifier used in the CDN path
	 * @param config.alt - Alt/title text (typically the product name)
	 * @param config.sizeSettings - Breakpoint-keyed dimension map (defaults to productPage)
	 */
	constructor(config: IThronProductImageConfig, transformationObject: IProductTransformationObject) {
		const thronProductsConfig = getFrontEndConfig()?.app?.thron?.products;
		const THRON_PRODUCT_CLIENT_ID = thronProductsConfig?.clientId;
		const THRON_PRODUCT_PKEY = thronProductsConfig?.pkey;

		this.imageUrl = `https://${THRON_PRODUCT_CLIENT_ID}-cdn.thron.com/api/v1/content-delivery/shares/${THRON_PRODUCT_PKEY}/contents/${config.contentId}/image/${config.productId}`;
		this.alt = config.alt;
		this.transformationObject = transformationObject || PRODUCT_IMAGES_SETTINGS.productPage;
	}

	/**
	 * The transformation object may not contain a value for all the system breakpoints
	 * if the given breakpoint exists it is returned, otherwise we return the nearest one.
	 * @param breakPoint
	 * @private
	 */
	private findNearestAvailableBreakpoint(breakPoint: string): string {
		const systemBreakPoints = ViewportMgr.getBreakpoints(true);
		const breakPointIndex = systemBreakPoints.indexOf(breakPoint as TBreakpoint);

		for (let i = breakPointIndex + 1; i--; i >= 0) {
			const currentBreakpoint = systemBreakPoints[i];

			if (currentBreakpoint in this.transformationObject) {
				return currentBreakpoint;
			}
		}

		return Object.keys(this.transformationObject)[0];
	}

	/** Returns the transformation dimensions for the nearest configured breakpoint. */
	private getNearestAvailableTransformation(breakPoint?: string) {
		const transformation = this.transformationObject[this.findNearestAvailableBreakpoint(breakPoint)];

		return typeof transformation === 'object' && transformation !== null
			? transformation
			: undefined;
	}

	/** Returns the Thron CDN image URL, resized for the given breakpoint and DPI when provided. */
	getUrl(breakPoint?: string, dpi: number = 1): string {
		if (!breakPoint) {
			return this.imageUrl;
		}

		const nearestAvailableTransformation = this.getNearestAvailableTransformation(breakPoint);

		if (!nearestAvailableTransformation?.sw) {
			return this.imageUrl;
		}

		const w = Math.round(nearestAvailableTransformation.sw * dpi);

		return `${this.imageUrl}?w=${w}`;
	}

	/** Returns an inline SVG placeholder with correct aspect ratio to reserve layout space. */
	getPlaceholderUrl(breakPoint?: string): string {
		const nearestAvailableTransformation = this.getNearestAvailableTransformation(breakPoint);

		if (!nearestAvailableTransformation?.sw || !nearestAvailableTransformation?.sh) {
			return this.imageUrl;
		}

		return generateImagePlaceholder(nearestAvailableTransformation.sw, nearestAvailableTransformation.sh);
	}

	/** Returns the image width for the given breakpoint from sizeSettings. */
	getWidth(breakPoint?: string): string {
		const nearestAvailableTransformation = this.getNearestAvailableTransformation(breakPoint);

		return nearestAvailableTransformation?.sw ? String(nearestAvailableTransformation.sw) : '';
	}

	/** Returns the image height for the given breakpoint from sizeSettings. */
	getHeight(breakPoint?: string): string {
		const nearestAvailableTransformation = this.getNearestAvailableTransformation(breakPoint);

		return nearestAvailableTransformation?.sh ? String(nearestAvailableTransformation.sh) : '';
	}

	/** Returns the alt text as title. */
	getTitle(): string {
		return this.alt;
	}

	/** Returns the alt text for accessibility. */
	getAlt(): string {
		return this.alt;
	}
}

export default ThronProductImage;
